import { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { OwlCarousel as ReactOwlCarousel } from "../../Layout/Common/owlCarousel";

const BANNER_URL = process.env.REACT_APP_BANNER_SLIDER_URL;

const BREAKPOINTS = [0, 600, 991, 1200];

// A height of 0 means the image keeps its own proportions - the behaviour before the
// setting existed - so a group that never sets one renders byte-identical markup.
const FALLBACK = {
  items: { 0: 1, 600: 2, 991: 3, 1200: 4 },
  margin: 15,
  autoplay: true,
  speed: 5000,
  height: 0,
  fit: "cover",
};

const FIT_MODES = ["cover", "contain"];

// One height is published and scaled down here, rather than four heights being
// configured: a strip that is 220px on a desktop column would be nearly square on a
// phone, and these ratios are the same for every group.
const HEIGHT_SCALE = { narrow: 0.6, mobile: 0.75, wide: 1 };

const positiveInt = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
};

// Every field is read defensively and falls back on its own: this component overwrites
// nothing, so one mistyped number must not blank the hero.
const readSettings = (raw) => {
  if (!raw || typeof raw !== "object") return FALLBACK;

  const items = {};
  BREAKPOINTS.forEach((bp) => {
    items[bp] = positiveInt(raw.items && raw.items[bp], FALLBACK.items[bp]);
  });

  const height = Number(raw.height);

  return {
    items,
    margin: Number.isFinite(Number(raw.margin)) ? Number(raw.margin) : FALLBACK.margin,
    autoplay: typeof raw.autoplay === "boolean" ? raw.autoplay : FALLBACK.autoplay,
    speed: positiveInt(raw.speed, FALLBACK.speed),
    height: Number.isFinite(height) && height > 0 ? Math.floor(height) : 0,
    fit: FIT_MODES.includes(raw.fit) ? raw.fit : FALLBACK.fit,
  };
};

// The published file is a list of groups, each carrying its own name, settings and
// banners. Array order is render order. An emptied group is how a row is switched off,
// so it is dropped rather than drawn as an empty carousel.
const readGroups = (data) => {
  if (!Array.isArray(data)) return [];

  return data
    .filter((group) => group && typeof group === "object" && Array.isArray(group.items) && group.items.length > 0)
    .map((group, index) => ({
      name: typeof group.name === "string" && group.name.trim() ? group.name.trim() : `group-${index}`,
      rows: group.items,
      settings: readSettings(group.settings),
    }));
};

// Clamp requested items to what we actually have, so Owl never reserves space for slides
// that don't exist; and only loop when there is something off-screen to rotate in.
const buildOptions = (settings, count, isMobile) => {
  const visible = isMobile ? settings.items[0] : settings.items[1200];
  const loops = count > visible;

  const responsive = {};
  BREAKPOINTS.forEach((bp) => {
    responsive[bp] = { items: Math.min(settings.items[bp], count) };
  });

  return {
    nav: false,
    margin: settings.margin,
    autoplayHoverPause: true,
    autoplayTimeout: settings.speed,
    items: Math.min(settings.items[1200], count),
    loop: loops,
    autoplay: loops && settings.autoplay,
    dots: loops,
    responsive,
  };
};

export const TopBannerPanel = () => {
  const [banners, setBanners] = useState([]);
  const { homeBanner, setHomeBanner } = useContext(GlobalContext);

  // Owl reads `loop` once at init and ignores it in `responsive`,
  // so we track the breakpoint ourselves and remount on a flip
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 991);

  // Tracked separately, and deliberately NOT part of the carousel key: image height
  // changing must not tear down and rebuild the carousel mid-scroll.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 600);

  useEffect(() => {
    const wide = window.matchMedia("(max-width: 990px)");
    const narrow = window.matchMedia("(max-width: 599px)");

    const onWide = (e) => setIsMobile(e.matches);
    const onNarrow = (e) => setIsNarrow(e.matches);

    setIsMobile(wide.matches);
    setIsNarrow(narrow.matches);

    wide.addEventListener("change", onWide);
    narrow.addEventListener("change", onNarrow);

    return () => {
      wide.removeEventListener("change", onWide);
      narrow.removeEventListener("change", onNarrow);
    };
  }, []);

  useEffect(() => {
    if (homeBanner) {
      setBanners(readGroups(homeBanner));
      return;
    }

    if (!window.__wesBannerPromise && !BANNER_URL) return;

    let cancelled = false;
    const source = window.__wesBannerPromise ?? fetch(BANNER_URL, { cache: "no-cache" }).then((res) => res.json());

    source
      .then((data) => {
        if (cancelled || !data) return;
        setHomeBanner(data);
        setBanners(readGroups(data));
      })
      .catch(() => {
        /* banner is non-critical: fail silently rather than blanking the hero */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scale = isNarrow ? HEIGHT_SCALE.narrow : isMobile ? HEIGHT_SCALE.mobile : HEIGHT_SCALE.wide;

  const groups = useMemo(
    () =>
      banners.map((group) => ({
        ...group,
        options: buildOptions(group.settings, group.rows.length, isMobile),
        // No height set means no style at all, not a style of "auto".
        imageStyle:
          group.settings.height > 0
            ? { height: `${Math.round(group.settings.height * scale)}px`, width: "100%", objectFit: group.settings.fit }
            : undefined,
      })),
    [banners, isMobile, scale],
  );

  const renderSlide = (row, index, isPriority, imageStyle) => (
    <div className="item" key={`${row.URL}-${index}`}>
      <Link to={row.URL}>
        <img
          src={row.IMG_PATH}
          className="img-fluid"
          alt="banner"
          style={imageStyle}
          fetchpriority={isPriority && index === 0 ? "high" : undefined}
          decoding="async"
          onError={(e) => {
            e.currentTarget.src = process.env.REACT_APP_NoImage;
          }}
        />
      </Link>
    </div>
  );

  if (groups.length === 0) return null;

  return (
    <section className="banner-section">
      <div className="container-fluid container-lg">
        {groups.map((group, index) => (
          <div key={group.name} className={index === groups.length - 1 ? "col-12" : "col-12 mb-4"}>
            <ReactOwlCarousel
              key={`${group.name}-${isMobile ? "m" : "d"}-${group.rows.length}`}
              className={index === 0 ? "owl-theme" : "home-banner-two owl-theme"}
              {...group.options}
            >
              {group.rows.map((row, i) => renderSlide(row, i, index === 0, group.imageStyle))}
            </ReactOwlCarousel>
          </div>
        ))}
      </div>
    </section>
  );
};
